import { Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { Trash2, Plus, Minus, MessageCircle, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, remove, updateQty, clear, totalItems } = useCart();

  // WhatsApp number — change to actual number when you have one (with country code, no +)
  const WHATSAPP_NUMBER = "23012345678";

  const buildMessage = () => {
    const lines = [
      "Bonjour RWANA BIZIN LEGA,",
      "",
      "Je souhaite commander :",
      ...items.map((i) => `• ${i.qty}× ${i.name}${i.variant ? ` (${i.variant})` : ""} — ${i.price}`),
      "",
      `Total : ${totalItems} article${totalItems > 1 ? "s" : ""}`,
      "",
      "Pouvez-vous me confirmer le total et la livraison ?",
      "Merci 🌿",
    ];
    return encodeURIComponent(lines.join("\n"));
  };

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${buildMessage()}`;

  return (
    <div data-testid="cart-page">
      <section className="border-b-2 border-[#1A1A18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="font-mono-label text-[#4A7A3D] mb-3">Panier</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.0]">Ta commande.</h1>
        </div>
      </section>

      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {items.length === 0 ? (
            <div className="py-20 text-center" data-testid="cart-empty">
              <ShoppingBag size={48} className="mx-auto text-[#E0D8CE]" />
              <p className="font-quote text-2xl text-[#4B4B48] mt-6">Ton panier est vide.</p>
              <Link to="/boutique" className="inline-block mt-8 bg-[#4A7A3D] text-[#FAF8F3] px-6 py-3 font-mono-label hover:bg-[#3E6633]">
                Découvrir la boutique →
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 border-2 border-[#1A1A18]">
                {items.map((i, idx) => (
                  <div key={`${i.slug}-${i.variant}`} className={`flex gap-4 p-4 ${idx > 0 ? "border-t border-[#E0D8CE]" : ""}`} data-testid={`cart-item-${i.slug}`}>
                    <div className="w-24 h-24 flex-shrink-0 bg-[#F5EFEB]">
                      {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1">
                      <Link to={`/produit/${i.slug}`} className="font-display text-xl hover:text-[#4A7A3D] block leading-tight">{i.name}</Link>
                      {i.variant && <div className="font-mono-label text-[#4B4B48] mt-1">Taille : {i.variant}</div>}
                      <div className="font-display text-lg text-[#4A7A3D] mt-1">{i.price}</div>
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => updateQty(i.slug, i.variant, i.qty - 1)} className="border border-[#1A1A18] p-1.5 hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid={`cart-minus-${i.slug}`}><Minus size={12} /></button>
                        <span className="font-mono-label">{i.qty}</span>
                        <button onClick={() => updateQty(i.slug, i.variant, i.qty + 1)} className="border border-[#1A1A18] p-1.5 hover:bg-[#1A1A18] hover:text-[#FAF8F3]" data-testid={`cart-plus-${i.slug}`}><Plus size={12} /></button>
                      </div>
                    </div>
                    <button onClick={() => remove(i.slug, i.variant)} className="self-start text-red-700 hover:bg-red-50 p-2" aria-label="Retirer" data-testid={`cart-remove-${i.slug}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <div className="border-t border-[#E0D8CE] p-4 flex justify-between items-center">
                  <button onClick={clear} className="font-mono-label text-[#4B4B48] hover:text-red-700" data-testid="cart-clear">Vider le panier</button>
                  <Link to="/boutique" className="font-mono-label text-[#4A7A3D] hover:text-[#1A1A18]" data-testid="cart-continue">Continuer mes achats →</Link>
                </div>
              </div>

              <div className="bg-[#1A1A18] text-[#FAF8F3] p-8">
                <div className="font-mono-label text-[#C4944A] mb-3">Récapitulatif</div>
                <div className="font-display text-3xl">{totalItems} article{totalItems > 1 ? "s" : ""}</div>
                <p className="text-[#FAF8F3]/70 mt-4 text-sm">
                  Pour finaliser ta commande, clique ci-dessous. Tu seras redirigé sur WhatsApp avec le détail de ta commande pré-rempli. Nous confirmons le total final, le mode de paiement et la livraison directement avec toi.
                </p>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#4A7A3D] text-[#FAF8F3] px-6 py-4 mt-6 font-mono-label flex items-center justify-center gap-2 hover:bg-[#C4944A] hover:text-[#1A1A18] w-full"
                  data-testid="cart-checkout-whatsapp"
                >
                  <MessageCircle size={16} /> Commander via WhatsApp
                </a>
                <a
                  href={`mailto:contact@rwanabizinlega.mu?subject=Commande%20RWANA&body=${buildMessage()}`}
                  className="border-2 border-[#FAF8F3]/40 px-6 py-3 mt-3 font-mono-label flex items-center justify-center gap-2 hover:border-[#FAF8F3] w-full"
                  data-testid="cart-checkout-email"
                >
                  …ou par email
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
